/**
 * User Preferences Service
 * Manages user preferences stored in localStorage
 */

const PREFERENCES_KEY = "excalidraw-user-preferences";

export interface UserPreferences {
  theme: "light" | "dark";
  canvasBackground: string;
  fontPresets: {
    small: number;
    medium: number;
    large: number;
    extraLarge: number;
  };
  language: string;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  theme: "light",
  canvasBackground: "#ffffff",
  fontPresets: {
    small: 16,
    medium: 20,
    large: 28,
    extraLarge: 36,
  },
  language: "en",
};

/**
 * Get user preferences from localStorage
 */
export const getUserPreferences = (): UserPreferences => {
  try {
    const stored = localStorage.getItem(PREFERENCES_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to ensure all properties exist
      return {
        ...DEFAULT_PREFERENCES,
        ...parsed,
        fontPresets: {
          ...DEFAULT_PREFERENCES.fontPresets,
          ...parsed.fontPresets,
        },
      };
    }
  } catch (error) {
    console.error("Error loading user preferences:", error);
  }
  return DEFAULT_PREFERENCES;
};

/**
 * Save user preferences to localStorage
 */
export const saveUserPreferences = (
  preferences: Partial<UserPreferences>,
): void => {
  try {
    const current = getUserPreferences();
    const updated = {
      ...current,
      ...preferences,
      fontPresets: {
        ...current.fontPresets,
        ...(preferences.fontPresets || {}),
      },
    };
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error("Error saving user preferences:", error);
  }
};

/**
 * Reset preferences to defaults
 */
export const resetUserPreferences = (): void => {
  try {
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(DEFAULT_PREFERENCES));
  } catch (error) {
    console.error("Error resetting user preferences:", error);
  }
};

/**
 * Get font preset sizes
 */
export const getFontPresets = () => {
  const prefs = getUserPreferences();
  return prefs.fontPresets;
};
